import { Outlet, useLocation } from '@remix-run/react'
import booleanContains from '@turf/boolean-contains'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  aCheckedListings,
  aDrawMode,
  aDrawnFeature,
  aHoverFeature,
  aMapCurrentCount,
  aMapDrawIsOpen,
  aGoogleStreetViewVisible,
  aOrganization,
  aParcelDataResidentialVisible,
  aParcelDataVisible,
  aSelectedListing,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  CheckAllListings,
  GoogleBuildingsLayer,
  GoogleDataLayers,
  GoogleGeographiesLayer,
  GoogleListingHoverPopup,
  GoogleListingLayers,
  GoogleZoomSafeClusterMarkers,
  GoogleParcelPopup,
  GoogleParcelsLayer,
  isStoredCircleFeature,
  MapCardList,
  MapDrawControl,
  MapFilterSheet,
  MapLayersSheet,
  MapLegend,
  MapListingCountLoading,
  MapNavigationControl,
  MapReportActions,
  MapSavedSearches,
  MapSearch,
  MapSimplifiedModeWarning,
  simplifyDrawPolygonFeature,
  MapViewControls,
  useDrawnFeatureBackNavigationRestore,
  useGoogleListingClickHandlers,
  useGoogleMapLayerVisibility,
  useMapListings,
  useMapProperties,
  type AppMapRef,
  type GoogleParcelClick,
  type MapOutletContextData,
} from '~/components'
import {
  MapView,
  useCurrentMapView,
  useResetMapState,
  useRouteMeta,
} from '~/hooks'
import { cn } from '~/utils'
import ListingsMap from './Map'

type SelectedParcelPopup = GoogleParcelClick & {
  parcelId: string | null
}

function getSelectedParcelId(parcel: GoogleParcelClick) {
  const properties = parcel.feature.properties ?? {}

  return (
    (typeof properties.ll_uuid === 'string' && properties.ll_uuid) ||
    (typeof properties.parcelnumb === 'string' && properties.parcelnumb) ||
    null
  )
}

export default function Container() {
  const mapRef = useRef<AppMapRef | null>(null)
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const currentMapView = useCurrentMapView()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const selectedListing = useAtomValue(aSelectedListing)
  const [hoverFeature, setHoverFeature] = useAtom(aHoverFeature)
  const checkedListings = useAtomValue(aCheckedListings)
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const passiveDrawnFeature = useMemo(
    () => simplifyDrawPolygonFeature(drawnFeature),
    [drawnFeature]
  )
  const [selectedParcelPopup, setSelectedParcelPopup] =
    useState<SelectedParcelPopup | null>(null)
  const setDrawIsOpen = useSetAtom(aMapDrawIsOpen)
  const isStreetViewVisible = useAtomValue(aGoogleStreetViewVisible)
  const {
    hideMovingLayers: hideMapLayers,
    hideOptionalDeckLayers,
    useZoomSafeListingPresentation,
  } = useGoogleMapLayerVisibility()
  const hideAppMapLayers = hideMapLayers || isStreetViewVisible
  const hideListingPresentation =
    (hideMapLayers && !useZoomSafeListingPresentation) || isStreetViewVisible
  const hideParcelLayers =
    hideOptionalDeckLayers ||
    isStreetViewVisible ||
    Boolean(drawMode) ||
    Boolean(drawnFeature)
  const isActivelyDrawing =
    drawMode === 'draw_circle' ||
    drawMode === 'draw_freehand' ||
    drawMode === 'draw_polygon'
  const parcelDataVisible = useAtomValue(aParcelDataVisible)
  const parcelDataResidentialVisible = useAtomValue(
    aParcelDataResidentialVisible
  )
  const location = useLocation()
  const { setRouteMeta } = useRouteMeta()
  const { onBackgroundClick, onListingClick } = useGoogleListingClickHandlers()
  useResetMapState()
  useDrawnFeatureBackNavigationRestore()

  useEffect(() => {
    setRouteMeta({
      renderedMainMap: true,
    })
  }, [location.key])

  useEffect(() => {
    return () => setSelectedListing(null)
  }, [])

  useEffect(() => {
    if (hideAppMapLayers) {
      setHoverFeature(null)
      setSelectedParcelPopup(null)
    }
  }, [hideAppMapLayers, setHoverFeature])

  useEffect(() => {
    if (hideParcelLayers) {
      setSelectedParcelPopup(null)
    }
  }, [hideParcelLayers])

  const { properties, totalCount, isLoadingProperties, isFetchingProperties } =
    useMapProperties()

  const {
    listings,
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    count: drawnListingCount,
  } = useMapListings({})

  const setCurrentMapCount = useSetAtom(aMapCurrentCount)

  useEffect(() => {
    if (!passiveDrawnFeature || typeof drawnListingCount !== 'number') {
      return
    }

    setCurrentMapCount(drawnListingCount)
  }, [drawnListingCount, passiveDrawnFeature, setCurrentMapCount])

  const visibleMapProperties = useMemo(() => {
    if (hideListingPresentation) return []
    if (!passiveDrawnFeature) return properties

    return properties.filter((property) =>
      booleanContains(passiveDrawnFeature, property.location)
    )
  }, [hideListingPresentation, passiveDrawnFeature, properties])

  return (
    <ListingsMap
      mapRef={mapRef}
      layers={
        <>
          {!hideAppMapLayers && !hideOptionalDeckLayers && (
            <GoogleGeographiesLayer />
          )}
          {!hideAppMapLayers && (
            <>
              <GoogleBuildingsLayer />
              {organization!.options.dataLayersEnabled && <GoogleDataLayers />}
              {organization!.options.parcelDataEnabled && !hideParcelLayers && (
                <>
                  <GoogleParcelsLayer
                    enabled={parcelDataVisible && !drawMode}
                    showResidentialParcels={parcelDataResidentialVisible}
                    excludeStackedParcels
                    onMapClick={() => setSelectedParcelPopup(null)}
                    onParcelClick={(parcel) => {
                      setSelectedParcelPopup({
                        ...parcel,
                        parcelId: getSelectedParcelId(parcel),
                      })
                    }}
                  />
                  {selectedParcelPopup?.latLng ? (
                    <GoogleParcelPopup
                      latitude={selectedParcelPopup.latLng.lat}
                      longitude={selectedParcelPopup.latLng.lng}
                      parcelId={selectedParcelPopup.parcelId}
                      onClose={() => setSelectedParcelPopup(null)}
                    />
                  ) : null}
                </>
              )}
            </>
          )}
          {organization!.options.listingsEnabled && (
            <>
              {!useZoomSafeListingPresentation && (
                <GoogleListingLayers
                  disableListingInteraction={isActivelyDrawing}
                  passiveDrawnFeature={
                    !hideListingPresentation && !drawMode
                      ? passiveDrawnFeature
                      : null
                  }
                  onPassiveDrawnFeatureClick={() => {
                    if (!drawnFeature) return

                    setDrawIsOpen(true)
                    setDrawMode(
                      isStoredCircleFeature(drawnFeature)
                        ? 'draw_circle'
                        : 'simple_select'
                    )
                  }}
                  properties={visibleMapProperties}
                  selectedListingId={
                    hideListingPresentation ? null : (selectedListing?._id ?? null)
                  }
                  showImageMarkers={!hideListingPresentation}
                  onListingClick={
                    hideListingPresentation ? undefined : onListingClick
                  }
                  onBackgroundClick={() => {
                    if (hideListingPresentation) return
                    if (drawMode === 'simple_select') {
                      setDrawMode(null)
                    }
                    onBackgroundClick()
                  }}
                />
              )}
              <GoogleZoomSafeClusterMarkers
                active={useZoomSafeListingPresentation}
                captureSnapshot={!hideMapLayers && !isStreetViewVisible}
                disableListingInteraction={isActivelyDrawing}
                properties={visibleMapProperties}
              />
              {!hideAppMapLayers && hoverFeature ? (
                <GoogleListingHoverPopup listing={hoverFeature} />
              ) : null}
            </>
          )}
        </>
      }>
      {!isStreetViewVisible && (
        <>
          <div className="absolute left-2 top-2 z-50 flex gap-2 md:left-4 md:top-4 lg:left-16">
            <MapNavigationControl />
            {organization!.options.listingsEnabled && (
              <div className="flex gap-2">
                <MapReportActions
                  listings={listings}
                  currentMapCount={currentMapCount}
                />
                <MapDrawControl className="hidden sm:flex" />
                {checkedListings.length > 0 && (
                  <CheckAllListings
                    listings={listings}
                    currentMapCount={currentMapCount}
                  />
                )}
              </div>
            )}
          </div>

          {organization!.options.listingsEnabled && (
            <MapListingCountLoading
              className="absolute bottom-2 left-1/2 z-50 -translate-x-1/2 md:bottom-4 lg:bottom-auto lg:top-4"
              isLoading={isLoadingProperties || isFetchingProperties}
              count={currentMapCount}
              totalCount={totalCount}
            />
          )}

          <div
            className={cn(
              'absolute right-14 top-2 z-50 flex gap-2 sm:flex-row md:right-16 md:top-4',
              user ? 'flex-col-reverse' : 'flex-row'
            )}>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {organization!.options.listingsEnabled && (
                <>
                  <MapSearch />
                  <MapSavedSearches mapRef={mapRef} />
                </>
              )}
            </div>
            <MapLayersSheet />
            {organization!.options.listingsEnabled && <MapFilterSheet />}
          </div>
          <MapViewControls className="absolute right-2 top-2 z-50 flex flex-col gap-2 md:right-4 md:top-4" />
          {organization!.options.listingsEnabled && (
            <>
              <MapSimplifiedModeWarning />
              <MapLegend className="hidden sm:block" />
            </>
          )}
        </>
      )}

      {!isStreetViewVisible && organization!.options.listingsEnabled ? (
        <div
          className={cn(
            currentMapView === MapView.Map &&
              'absolute bottom-0 left-4 top-18 flex'
          )}>
          {currentMapView === MapView.Map && (
            <MapCardList
              listings={listings}
              isLoading={isLoading}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            />
          )}

          <Outlet
            context={
              {
                listings,
                isError,
                isLoading,
                isFetching,
                fetchNextPage,
                hasNextPage,
              } as MapOutletContextData
            }
          />
        </div>
      ) : (
        !isStreetViewVisible && <Outlet />
      )}
    </ListingsMap>
  )
}
